import { create } from 'zustand'
import { Agent, Team, Workflow, Message, SessionEntry, Run, PausedRunInfo } from '@/types/chat'
import type { ArtifactData } from '@/components/chat/artifact-card'

export interface TodoItem {
  content: string
  status: 'pending' | 'in_progress' | 'completed'
}

/** Live context-window state for the NEXT turn. */
export interface ContextUsage {
  input_tokens?: number
  model_id?: string
  window?: number
  /** Token count at which Kael auto-compacts; absent until the backend sends it. */
  compact_at_tokens?: number
}

/**
 * Chat UI state — messages, sessions, streaming, entity selection.
 * Context (clientId, projectId) lives in settings-store, not here.
 */

interface ChatState {
  // Agents, Teams and Workflows
  agents: Agent[]
  teams: Team[]
  workflows: Workflow[]
  selectedAgent: Agent | null
  selectedTeam: Team | null
  selectedWorkflow: Workflow | null

  // User
  currentUser: string | null

  // Messages
  messages: Message[]
  runs: Run[]

  // Run pagination (GET /api/sessions/{id} is paginated by run — see use-load-session.ts)
  hasMoreRuns: boolean
  nextRunCursor: string | null
  isLoadingOlderRuns: boolean

  // Queued messages typed while a run is streaming — drained one-at-a-time once the
  // active run finishes cleanly (see useChatStream's queue-drain effect).
  queuedMessages: string[]

  // Context-window state for the next turn: seeded from session stats on load, then
  // refreshed by every `context_usage` the stream carries (tool events mid-run, RunCompleted).
  contextUsage: ContextUsage | null

  // True between ContextCompacting and ContextCompacted — the chat says "Compacting
  // context…" so a long summarisation pause doesn't read as a hang.
  isCompacting: boolean

  // Task list (Claude-Code-style todos from the write_todos tool)
  todos: TodoItem[]

  // Session
  currentSessionId: string | null
  sessions: SessionEntry[]
  sessionMetrics: any | null
  isLoadingSessions: boolean

  // UI State
  isStreaming: boolean
  isLoading: boolean
  isLoadingMessages: boolean
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
  isHealthy: boolean
  useWebsocket: boolean
  // Chat mode — Flash (fast) | Pro (deeper reasoning). Sent as `mode`; honoured by
  // agents with model variants (e.g. schedule) and by team chat.
  chatMode: 'flash' | 'pro'
  // Explicit model pick from the picker, or '' for "whatever this agent is configured with".
  // Sent as `model` and outranks chatMode on the server. Empty by default on purpose: the
  // configured model is the right answer for almost every turn, and a sticky non-default
  // selection is how a user ends up running every agent on a model they picked once, weeks ago.
  selectedModel: string

  // Paused HITL run info (ask_user_confirmation / ask_user_question)
  pausedRunInfo: PausedRunInfo | null

  // Artifact viewer
  selectedArtifact: ArtifactData | null
  // Per-session artifacts list panel toggle
  showArtifactsPanel: boolean

  // Actions
  setUseWebsocket: (useWebsocket: boolean) => void
  setChatMode: (mode: 'flash' | 'pro') => void
  setSelectedModel: (modelId: string) => void
  setAgents: (agents: Agent[]) => void
  setTeams: (teams: Team[]) => void
  setWorkflows: (workflows: Workflow[]) => void
  setSelectedAgent: (agent: Agent | null) => void
  setSelectedTeam: (team: Team | null) => void
  setSelectedWorkflow: (workflow: Workflow | null) => void
  setCurrentUser: (user: string | null) => void
  addMessage: (message: Message) => void
  addRecord: (message: Message) => void
  setMessages: (messages: Message[]) => void
  setRuns: (runs: Run[]) => void
  prependMessages: (messages: Message[]) => void
  setHasMoreRuns: (hasMore: boolean) => void
  setNextRunCursor: (cursor: string | null) => void
  setIsLoadingOlderRuns: (isLoading: boolean) => void
  enqueueMessage: (text: string) => void
  dequeueMessage: () => string | undefined
  takeAllQueued: () => string[]
  setContextUsage: (usage: ContextUsage | null) => void
  setIsCompacting: (isCompacting: boolean) => void
  setTodos: (todos: TodoItem[]) => void
  updateLastMessage: (content: string) => void
  updateLastMessageReasoning: (content: string) => void
  updateLastMessageData: (data: Partial<Message>) => void
  setCurrentSessionId: (id: string | null) => void
  setSessionMetrics: (metrics: any | null) => void
  setSessions: (sessions: SessionEntry[]) => void
  setIsLoadingSessions: (isLoading: boolean) => void
  setIsStreaming: (isStreaming: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setIsLoadingMessages: (isLoading: boolean) => void
  setIsHealthy: (isHealthy: boolean) => void
  setPausedRunInfo: (info: PausedRunInfo | null) => void
  setSelectedArtifact: (artifact: ArtifactData | null) => void
  setShowArtifactsPanel: (show: boolean) => void
  clearChat: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  agents: [],
  teams: [],
  workflows: [],
  selectedAgent: null,
  selectedTeam: null,
  selectedWorkflow: null,
  currentUser: null,
  messages: [],
  runs: [],
  hasMoreRuns: false,
  nextRunCursor: null,
  isLoadingOlderRuns: false,
  queuedMessages: [],
  contextUsage: null,
  isCompacting: false,
  todos: [],
  currentSessionId: null,
  sessions: [],
  sessionMetrics: null,
  isLoadingSessions: false,
  isStreaming: false,
  isLoading: false,
  isLoadingMessages: false,
  chatInputRef: { current: null },
  isHealthy: false,
  useWebsocket: false,
  chatMode: 'pro',
  selectedModel: '',
  pausedRunInfo: null,
  selectedArtifact: null,
  showArtifactsPanel: false,

  // Actions
  setUseWebsocket: (useWebsocket) => set({ useWebsocket }),
  setChatMode: (chatMode) => set({ chatMode }),
  setSelectedModel: (selectedModel) => set({ selectedModel }),
  setAgents: (agents) => set({ agents }),
  setTeams: (teams) => set({ teams }),
  setWorkflows: (workflows) => set({ workflows }),
  setSelectedAgent: (agent) => set({ selectedAgent: agent, selectedTeam: null, selectedWorkflow: null }),
  setSelectedTeam: (team) => set({ selectedTeam: team, selectedAgent: null, selectedWorkflow: null }),
  setSelectedWorkflow: (workflow) => set({ selectedWorkflow: workflow, selectedAgent: null, selectedTeam: null }),
  setCurrentUser: (currentUser) => set({ currentUser }),

  addMessage: (message) => set((state) => ({
    messages: [...state.messages, message]
  })),

  // A record (e.g. "Context compacted") can land mid-run, when the last message is the live
  // agent placeholder every updateLastMessage* call writes into — slot it in before that tail
  // rather than appending, which would redirect the rest of the stream into the record.
  addRecord: (message) => set((state) => {
    const messages = [...state.messages]
    const last = messages[messages.length - 1]
    const liveTail = state.isStreaming && (last?.role === 'agent' || last?.role === 'assistant')
    messages.splice(liveTail ? messages.length - 1 : messages.length, 0, message)
    return { messages }
  }),

  setMessages: (messages) => set({ messages }),
  setRuns: (runs) => set({ runs }),
  prependMessages: (messages) => set((state) => ({ messages: [...messages, ...state.messages] })),
  setHasMoreRuns: (hasMoreRuns) => set({ hasMoreRuns }),
  setNextRunCursor: (nextRunCursor) => set({ nextRunCursor }),
  setIsLoadingOlderRuns: (isLoadingOlderRuns) => set({ isLoadingOlderRuns }),

  enqueueMessage: (text) => set((state) => ({ queuedMessages: [...state.queuedMessages, text] })),

  dequeueMessage: () => {
    const q = get().queuedMessages
    if (q.length === 0) return undefined
    const [first, ...rest] = q
    set({ queuedMessages: rest })
    return first
  },

  takeAllQueued: () => {
    const q = get().queuedMessages
    if (q.length > 0) set({ queuedMessages: [] })
    return q
  },

  setContextUsage: (contextUsage) => set({ contextUsage }),

  setIsCompacting: (isCompacting) => set({ isCompacting }),

  setTodos: (todos) => set({ todos }),

  updateLastMessage: (content) => set((state) => {
    const messages = [...state.messages]
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      messages[messages.length - 1] = {
        ...lastMessage,
        content: lastMessage.content + content
      }
    }
    return { messages }
  }),

  updateLastMessageReasoning: (content) => set((state) => {
    const messages = [...state.messages]
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      messages[messages.length - 1] = {
        ...lastMessage,
        reasoning_content: (lastMessage.reasoning_content || '') + content
      }
    }
    return { messages }
  }),

  updateLastMessageData: (data) => set((state) => {
    const messages = [...state.messages]
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      const updatedMessage = { ...lastMessage }

      // Handle array fields: upsert tool_calls by tool_call_id (prevent doubling from
      // ToolCallStarted + ToolCallCompleted both appending the same tool), append others
      if (data.tool_calls) {
        const existing = updatedMessage.tool_calls || []
        const merged = [...existing]
        for (const tc of data.tool_calls) {
          const key = (tc as any).tool_call_id ?? (tc as any).id
          const idx = merged.findIndex(e => ((e as any).tool_call_id ?? (e as any).id) === key)
          if (idx >= 0) {
            merged[idx] = { ...merged[idx], ...tc }
          } else {
            merged.push(tc)
          }
        }
        updatedMessage.tool_calls = merged
      }
      if (data.images) {
        updatedMessage.images = [...(updatedMessage.images || []), ...data.images]
      }
      if (data.videos) {
        updatedMessage.videos = [...(updatedMessage.videos || []), ...data.videos]
      }
      if (data.audio) {
        updatedMessage.audio = [...(updatedMessage.audio || []), ...data.audio]
      }
      if (data.documents) {
        updatedMessage.documents = [...(updatedMessage.documents || []), ...data.documents]
      }
      if (data.files) {
        updatedMessage.files = [...(updatedMessage.files || []), ...data.files]
      }
      if (data.events) {
        updatedMessage.events = [...(updatedMessage.events || []), ...data.events]
      }

      // Handle extra_data merging
      if (data.extra_data) {
        const existingExtra = updatedMessage.extra_data || {}
        const newExtra = data.extra_data

        // Handle workflow_steps merging (upsert by step_id)
        let workflow_steps = existingExtra.workflow_steps || []
        if (newExtra.workflow_steps) {
          const newSteps = newExtra.workflow_steps
          const stepMap = new Map(workflow_steps.map((s: any) => [s.step_id, s]))

          newSteps.forEach((s: any) => {
            stepMap.set(s.step_id, { ...stepMap.get(s.step_id), ...s })
          })

          workflow_steps = Array.from(stepMap.values()).sort((a: any, b: any) => a.created_at - b.created_at)
        }

        // Accumulate agents_used (deduplicate by agent_name)
        const existingAgents: any[] = existingExtra.agents_used || []
        const newAgents: any[] = newExtra.agents_used || []
        const agentNames = new Set(existingAgents.map((a: any) => a.agent_name))
        const agents_used = [...existingAgents]
        for (const a of newAgents) {
          if (a.agent_name && !agentNames.has(a.agent_name)) {
            agentNames.add(a.agent_name)
            agents_used.push(a)
          }
        }

        updatedMessage.extra_data = {
          ...existingExtra,
          ...newExtra,
          workflow_steps,
          agents_used,
          references: [
            ...(existingExtra.references || []),
            ...(newExtra.references || [])
          ]
        }
      }

      // Handle scalar fields (exclude arrays we handled)
      const {
        tool_calls, images, videos, audio, documents, files, extra_data, events,
        ...otherData
      } = data

      Object.assign(updatedMessage, otherData)

      messages[messages.length - 1] = updatedMessage
    }
    return { messages }
  }),

  setCurrentSessionId: (id) => set({ currentSessionId: id }),
  setSessionMetrics: (metrics) => set({ sessionMetrics: metrics }),
  setSessions: (sessions) => set({ sessions }),
  setIsLoadingSessions: (isLoadingSessions) => set({ isLoadingSessions }),
  setIsStreaming: (isStreaming) => set({ isStreaming }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsLoadingMessages: (isLoadingMessages) => set({ isLoadingMessages }),
  setIsHealthy: (isHealthy) => set({ isHealthy }),
  setPausedRunInfo: (pausedRunInfo) => set({ pausedRunInfo }),
  setSelectedArtifact: (selectedArtifact) => set({ selectedArtifact }),
  setShowArtifactsPanel: (showArtifactsPanel) => set({ showArtifactsPanel }),

  clearChat: () => set({
    messages: [],
    queuedMessages: [],
    contextUsage: null,
    isCompacting: false,
    todos: [],
    selectedArtifact: null,
    showArtifactsPanel: false,
    currentSessionId: null,
    sessionMetrics: null,
    hasMoreRuns: false,
    nextRunCursor: null,
    isLoadingOlderRuns: false,
  })
}))
